import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trash, ArrowLeft, Pencil, Check, X, Loader2, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface PageHeaderProps {
  pageContentId: string;
  workbookId: string;
  title: string;
  onDelete?: () => void;
  onPageDeleted?: () => void; // Custom callback after successful deletion
  onUpdateTitle?: (id: string, title: string) => void; // New prop for title updates
  onGoBack?: () => void; // Optional callback for back navigation
  hideBackButton?: boolean; // Hide back button (e.g., in modals)
  // Loading states for button feedback
  isDeleting?: boolean;
  // PDF download props
  onDownloadPDF?: () => void;
  isGeneratingPDF?: boolean;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  pageContentId,
  workbookId,
  title,
  onDelete,
  onPageDeleted,
  onUpdateTitle,
  onGoBack,
  hideBackButton = false,
  isDeleting = false,
  onDownloadPDF,
  isGeneratingPDF = false,
}) => {
  const navigate = useNavigate();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-resize input to fit content
  useEffect(() => {
    if (inputRef.current && isEditingTitle) {
      // Create a temporary span to measure text width
      const span = document.createElement('span');
      span.style.visibility = 'hidden';
      span.style.position = 'absolute';
      span.style.whiteSpace = 'nowrap';
      // Match the responsive font size
      const isMobile = window.innerWidth < 768;
      span.style.fontSize = isMobile ? '1rem' : '1.25rem'; // text-base on mobile, text-xl on desktop
      span.style.fontWeight = 'bold';
      span.style.fontFamily = getComputedStyle(inputRef.current).fontFamily;
      span.textContent = tempTitle || 'Page title...'; // Use placeholder if empty
      
      document.body.appendChild(span);
      const textWidth = span.offsetWidth;
      document.body.removeChild(span);
      
      // Set input width with some padding
      const minWidth = 200; // Minimum width
      const maxWidth = 600; // Maximum width to prevent overflow
      const calculatedWidth = Math.min(Math.max(textWidth + 20, minWidth), maxWidth);
      
      inputRef.current.style.width = `${calculatedWidth}px`;
    }
  }, [tempTitle, isEditingTitle]);

  useEffect(() => {
    setTempTitle(title);
  }, [title]);

  useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleSaveTitle = () => {
    const newTitle = tempTitle.trim() || 'Untitled Document';
    if (onUpdateTitle) {
      onUpdateTitle(pageContentId, newTitle);
    }
    setIsEditingTitle(false);
  };

  const handleCancelEdit = () => {
    setTempTitle(title);
    setIsEditingTitle(false);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Limit to 50 characters
    if (value.length <= 50) {
      setTempTitle(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };
  
  const handleGoBack = () => {
    if (onGoBack) {
      onGoBack();
    } else {
      // Fallback to URL navigation if no callback provided
      navigate(`/project/${workbookId}/pages`);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {!hideBackButton && (
            <motion.button
              onClick={handleGoBack}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/30 hover:from-purple-50 hover:to-purple-100 dark:hover:from-purple-900/30 dark:hover:to-purple-800/30 text-blue-500 hover:text-purple-400 dark:text-blue-400 dark:hover:text-purple-300 border border-blue-200 hover:border-purple-200 dark:border-blue-800/50 dark:hover:border-purple-700/50 shadow-sm transition-all duration-300 flex items-center justify-center"
            >
              <ArrowLeft size={16} />
            </motion.button>
          )}
          {isEditingTitle ? (
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={tempTitle}
                onChange={handleTitleChange}
                onKeyDown={handleKeyDown}
                maxLength={50}
                className="text-base md:text-xl font-bold bg-transparent border-b-2 border-blue-500 outline-none text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-400 dark:from-blue-400 dark:to-purple-300 caret-blue-500 dark:caret-blue-400"
                placeholder="Page title..."
              />
              <motion.button
                onClick={handleSaveTitle}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="h-8 w-8 flex items-center justify-center rounded-md text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/30 border border-transparent hover:border-green-200 dark:hover:border-green-800/50 transition-colors"
                aria-label="Save title"
                title="Save title"
              >
                <Check size={16} />
              </motion.button>
              <motion.button
                onClick={handleCancelEdit}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="h-8 w-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 border border-transparent hover:border-red-200 dark:hover:border-red-800/50 transition-colors"
                aria-label="Cancel editing"
                title="Cancel editing"
              >
                <X size={16} />
              </motion.button>
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h1 className="text-base md:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-400 dark:from-blue-400 dark:to-purple-300">
                {title}
              </h1>
              <motion.button
                onClick={() => setIsEditingTitle(true)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="h-8 w-8 flex items-center justify-center rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-950/30 border border-transparent hover:border-gray-200 dark:hover:border-gray-800/50 transition-colors"
                aria-label="Edit page title"
                title="Edit page title"
                data-test-id="edit-title-button"
              >
                <Pencil size={14} />
              </motion.button>
              {onDownloadPDF && (
                <motion.button
                  onClick={onDownloadPDF}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className={cn(
                    "h-8 w-8 flex items-center justify-center rounded-md text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30 border border-transparent hover:border-blue-200 dark:hover:border-blue-800/50 transition-colors",
                    isGeneratingPDF && "opacity-75"
                  )}
                  disabled={isGeneratingPDF}
                  title="Download as PDF"
                  aria-label="Download document as PDF"
                >
                  {isGeneratingPDF ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Download size={16} />
                  )}
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className={cn(
                  "h-8 w-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 border border-transparent hover:border-red-200 dark:hover:border-red-800/50 transition-colors",
                  isDeleting && "opacity-75"
                )}
                onClick={onDelete}
                disabled={isDeleting}
                data-test-id="delete-page-button"
                title="Delete page permanently"
              >
                {isDeleting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Trash size={16} />
                )}
              </motion.button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PageHeader; 